import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaCheckCircle,
    FaDatabase,
    FaEye,
    FaFileAlt,
    FaFingerprint,
    FaGlobe,
    FaKey,
    FaLock,
    FaShieldAlt,
    FaTrashAlt,
    FaUserShield
} from "react-icons/fa";
import {
    TiArrowForward,
    TiArrowRight,
    TiArrowUp
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const PrivacyPolicy = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const privacyPrinciples = [
    {
      id: "PP-01",
      title: "Data Collection",
      subtitle: "Minimal Footprint Protocol",
      desc: "We collect only essential data required for seamless platform operations. All intake is transparent, purpose-limited, and aligned with GDPR/CCPA standards.",
      icon: <FaDatabase />,
      highlights: ["Purpose-Limited", "Transparent Intake", "Minimal Data"]
    },
    {
      id: "PP-02",
      title: "Usage Rights",
      subtitle: "Ecosystem Intelligence",
      desc: "Your data powers personalized experiences and ecosystem analytics without ever being sold. Usage is strictly internal and for your direct benefit.",
      icon: <FaEye />,
      highlights: ["No Third-Party Sales", "Internal Analytics", "User Benefit"]
    },
    {
      id: "PP-03",
      title: "Security Architecture",
      subtitle: "AES-256 Encryption Standard",
      desc: "All data is protected by military-grade AES-256 encryption, stored in cold-secure vaults, and monitored by 24/7 biometric audit systems.",
      icon: <FaLock />,
      highlights: ["AES-256 Encryption", "Cold Storage", "Biometric Audit"]
    },
    {
      id: "PP-04",
      title: "User Sovereignty",
      subtitle: "Full Control Protocol",
      desc: "You retain complete control over your data. Request access, export, or permanent deletion at any time through our institutional Data Officer portal.",
      icon: <FaUserShield />,
      highlights: ["Data Export", "Deletion Rights", "Full Access"]
    }
  ];

  const protectionLifecycle = [
    { title: "Data Intake", desc: "Transparent collection with explicit consent and purpose limitation.", icon: <FaFileAlt /> },
    { title: "Encryption Layer", desc: "Immediate AES-256 encryption applied at point of entry.", icon: <FaKey /> },
    { title: "Secure Storage", desc: "Data stored in geographically distributed cold-secure vaults.", icon: <FaDatabase /> },
    { title: "Access Control", desc: "Biometric and multi-factor authentication for all data access.", icon: <FaFingerprint /> },
    { title: "Data Deletion", desc: "Permanent removal protocols with cryptographic verification.", icon: <FaTrashAlt /> }
  ];

  const enforcementProducts = [
    {
      title: "Zero Comply",
      id: "7",
      role: "Regulatory Engine",
      desc: "Automated KYC/AML synchronization engine utilizing the high-speed ReguSync protocol.",
      icon: <FaFingerprint />
    },
    {
      title: "Zero Supervise",
      id: "8",
      role: "Oversight Node",
      desc: "Real-time network monitoring and automated reporting portal for institutional transparency.",
      icon: <FaShieldAlt />
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/privacy-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="privacyHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Data Sovereignty"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Grade Privacy & Encryption Protocol
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Privacy Principles Bento */}
        <div className="mb-48">
           <div className="mb-16 flex flex-col md:flex-row justify-between items-end gap-8">
              <div className="max-w-xl">
                 <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-[0.4em] mb-4">Core Directives</div>
                 <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight">Privacy Principles</h2>
              </div>
              <p className="text-gray-300 italic text-sm max-w-xs md:text-right">
                 Institutional-grade data governance for the Zero-Ecosystem alliance.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {privacyPrinciples.map((principle, index) => (
                <motion.div
                  key={principle.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group rounded-[3rem] bg-white/5 border border-white/10 hover:border-indigo-500/30 transition-all hover:bg-white/10 overflow-hidden flex flex-col p-10"
                  ref={(el) => (sectionRefs.current[index] = el)}
                >
                  <div className="absolute top-8 right-10 text-4xl text-indigo-500/20 group-hover:scale-110 transition-transform">
                     {principle.icon}
                  </div>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-2">{principle.id}</div>
                  <h3 className="text-2xl font-bold text-white mb-2 tracking-widest">{principle.title}</h3>
                  <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em] mb-6">{principle.subtitle}</div>
                  <p className="text-gray-300 text-xs leading-relaxed mb-8 flex-grow">{principle.desc}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                     {principle.highlights.map((h, i) => (
                       <span key={i} className="text-[8px] font-bold text-gray-600 border border-white/5 px-3 py-1 rounded-full uppercase tracking-widest group-hover:border-indigo-500/20 group-hover:text-gray-300 transition-colors">
                          {h}
                       </span>
                     ))}
                  </div>
                </motion.div>
              ))}
           </div>
        </div>

        {/* Data Protection Lifecycle */}
        <div className="flex flex-col lg:flex-row gap-24 items-center mb-48">
           <div className="w-full lg:w-1/2">
              <div className="text-[10px] font-bold text-purple-500 uppercase tracking-[0.4em] mb-4">Protection Lifecycle</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight mb-12">Data <span className="text-purple-500">Governance</span> Protocol</h2>
              
              <div className="space-y-12 relative">
                 <div className="absolute left-6 top-8 bottom-8 w-[1px] bg-white/10" />
                 {protectionLifecycle.map((step, idx) => (
                   <motion.div 
                     key={idx}
                     onViewportEnter={() => setActiveStep(idx)}
                     className={`relative pl-16 group cursor-default transition-opacity duration-700 ${activeStep === idx ? 'opacity-100' : 'opacity-30'}`}
                   >
                      <div className={`absolute left-0 top-0 w-12 h-12 rounded-full border flex items-center justify-center transition-all ${activeStep === idx ? 'bg-purple-600 border-purple-400 shadow-[0_0_20px_rgba(147,51,234,0.5)]' : 'bg-black border-white/20'}`}>
                         <span className={`text-lg ${activeStep === idx ? 'text-white' : 'text-white/20'}`}>{step.icon}</span>
                      </div>
                      <h4 className="text-xl font-bold text-white mb-2 tracking-widest uppercase">{step.title}</h4>
                      <p className="text-gray-500 text-sm italic">{step.desc}</p>
                   </motion.div>
                 ))}
              </div>
           </div>

           <div className="w-full lg:w-1/2 p-2 rounded-[4rem] bg-gradient-to-br from-purple-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl relative group">
              <div className="bg-black/80 backdrop-blur-3xl p-16 relative">
                 <div className="flex items-center gap-4 mb-12">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-2xl">
                       <FaCheckCircle />
                    </div>
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.4em]">Encryption Status: ACTIVE</div>
                 </div>
                 <div className="grid grid-cols-2 gap-8">
                    {[
                      { l: "Encryption Standard", v: "AES-256 Military" },
                      { l: "Storage Protocol", v: "Cold-Secure Vault" },
                      { l: "Audit System", v: "24/7 Biometric" },
                      { l: "Compliance", v: "GDPR / CCPA" }
                    ].map((item, i) => (
                      <div key={i} className="flex flex-col gap-2">
                         <div className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">{item.l}</div>
                         <div className="text-sm font-bold text-white italic tracking-widest">{item.v}</div>
                      </div>
                    ))}
                 </div>
                 <div className="mt-16 pt-8 border-t border-white/5 flex items-center justify-center">
                    <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
                       <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        transition={{ duration: 2.5, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 shadow-[0_0_15px_rgba(147,51,234,0.5)]"
                       />
                    </div>
                 </div>
                 <div className="mt-4 flex justify-between text-[8px] font-bold text-gray-600 uppercase tracking-widest">
                    <span>Data Protection Active</span>
                    <span>100%</span>
                 </div>
              </div>
           </div>
        </div>

        {/* Ecosystem Enforcement */}
        <div className="mb-48">
           <div className="mb-16 text-center max-w-2xl mx-auto">
              <div className="text-[10px] font-bold text-cyan-500 uppercase tracking-[0.4em] mb-4">Enforcement Infrastructure</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight mb-8">Privacy Enforcement</h2>
              <p className="text-gray-300 text-sm leading-relaxed italic">
                 Powered by the institutional-grade Zero-Ecosystem compliance stack.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {enforcementProducts.map((prod, index) => (
                <motion.a
                  key={prod.id}
                  href={`/products/${prod.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-10 rounded-[3rem] bg-white/5 border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-900/10 transition-all flex flex-col items-center text-center group"
                >
                  <div className="w-20 h-20 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-4xl text-cyan-400 mb-8 group-hover:scale-110 transition-transform shadow-[0_0_30px_rgba(6,182,212,0.1)]">
                     {prod.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 tracking-widest uppercase">{prod.title}</h3>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.3em] mb-4">{prod.role}</div>
                  <p className="text-[11px] text-gray-500 leading-relaxed italic">{prod.desc}</p>
                  <motion.div whileHover={{ x: 5 }} className="mt-6 text-cyan-500">
                     <TiArrowForward className="text-2xl" />
                  </motion.div>
                </motion.a>
              ))}
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-purple-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[3rem] bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-5xl text-purple-400 mx-auto mb-16 shadow-[0_0_50px_rgba(147,51,234,0.2)]">
                <FaLock />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Initialize <span className="font-zentry italic text-purple-400 text-8xl uppercase tracking-tighter block mt-4">Data</span> Sovereignty
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                Your data. Your control. Full transparency and institutional-grade protection across the Zero-Ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   variant="purple"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   Download Privacy Archive
                 </GlassButton>
                 
                 <GlassButton
                   variant="gray"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   <div className="flex items-center gap-4 group">
                     Contact Data Officer <TiArrowRight className="text-2xl text-purple-400 group-hover:translate-x-2 transition-transform" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaGlobe /> GDPR COMPLIANT</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaShieldAlt /> CCPA CERTIFIED</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaLock /> AES-256 ENCRYPTED</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-full shadow-lg shadow-purple-500/10 cursor-pointer hover:bg-black/60 hover:border-purple-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-purple-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
